import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getHierarchyNodes } from "../services/api";
import { getTaxonomyHierarchyConfigByEntityType } from "../utils/taxonomyHierarchyConfigs";

/**
 * Modal to pick a parent from a lazy-loaded taxonomy tree
 * (same hierarchy as Admin → Hierarchies).
 */
const HierarchyParentPickerModal = ({
  isOpen,
  onClose,
  onSelect,
  entityType,
  currentEntityId = null,
  selectedParentId = "",
  title = null,
}) => {
  const cfg = getTaxonomyHierarchyConfigByEntityType(entityType);
  const [nodes, setNodes] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [loadedParentIds, setLoadedParentIds] = useState(() => new Set());
  const [loadingRoots, setLoadingRoots] = useState(false);
  const [loadingChildId, setLoadingChildId] = useState(null);
  const [error, setError] = useState("");
  const [pendingParentId, setPendingParentId] = useState(selectedParentId || "");
  const [searchQuery, setSearchQuery] = useState("");

  const disabledIds = useMemo(() => {
    const blocked = new Set();
    if (currentEntityId) blocked.add(String(currentEntityId));
    // Descendants of current entity cannot be parents (cycle prevention).
    let grew = true;
    while (grew) {
      grew = false;
      for (const n of nodes) {
        if (
          n.parent &&
          blocked.has(String(n.parent)) &&
          !blocked.has(String(n.id))
        ) {
          blocked.add(String(n.id));
          grew = true;
        }
      }
    }
    return blocked;
  }, [nodes, currentEntityId]);

  const loadRoots = useCallback(async () => {
    if (!cfg?.entityType) return;
    setLoadingRoots(true);
    setError("");
    try {
      const roots = await getHierarchyNodes(cfg.entityType, null, true);
      setNodes(roots || []);
      setLoadedParentIds(new Set([null]));
      setExpandedIds(new Set());
    } catch (e) {
      setError(e.message || `Failed to load ${cfg.entityLabelPlural}`);
      setNodes([]);
    } finally {
      setLoadingRoots(false);
    }
  }, [cfg?.entityType, cfg?.entityLabelPlural]);

  useEffect(() => {
    if (!isOpen) return;
    setPendingParentId(selectedParentId || "");
    setSearchQuery("");
    loadRoots();
  }, [isOpen, selectedParentId, loadRoots]);

  const loadChildren = async (parentId) => {
    if (!cfg?.entityType) return;
    if (loadedParentIds.has(parentId)) return;
    setLoadingChildId(parentId);
    try {
      const children = await getHierarchyNodes(cfg.entityType, parentId, true);
      setNodes((prev) => {
        const byId = new Map(prev.map((n) => [n.id, n]));
        (children || []).forEach((c) => byId.set(c.id, c));
        return Array.from(byId.values());
      });
      setLoadedParentIds((prev) => new Set(prev).add(parentId));
    } catch (e) {
      setError(e.message || "Failed to load children");
    } finally {
      setLoadingChildId(null);
    }
  };

  const toggleExpand = async (nodeId) => {
    if (expandedIds.has(nodeId)) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
      return;
    }
    await loadChildren(nodeId);
    setExpandedIds((prev) => new Set(prev).add(nodeId));
  };

  const childrenByParent = useMemo(() => {
    const map = {};
    nodes.forEach((n) => {
      const key = n.parent || null;
      if (!map[key]) map[key] = [];
      map[key].push(n);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
      )
    );
    return map;
  }, [nodes]);

  const roots = childrenByParent[null] || nodes.filter((n) => !n.parent);

  const searchLower = searchQuery.trim().toLowerCase();
  const searchMatches = useMemo(() => {
    if (!searchLower) return null;
    return nodes.filter((n) =>
      (n.name || "").toLowerCase().includes(searchLower)
    );
  }, [nodes, searchLower]);

  const handleConfirm = () => {
    if (pendingParentId && disabledIds.has(String(pendingParentId))) return;
    const node = nodes.find((n) => n.id === pendingParentId) || null;
    onSelect(pendingParentId || "", node);
    onClose();
  };

  const renderNode = (node, depth = 0) => {
    const id = String(node.id);
    const isDisabled = disabledIds.has(id);
    const isSelected = pendingParentId === node.id;
    const isExpanded = expandedIds.has(node.id);
    const childrenLoaded = loadedParentIds.has(node.id);
    const childList = childrenByParent[node.id] || [];
    const mayHaveChildren = childrenLoaded ? childList.length > 0 : true;
    const isLoadingChildren = loadingChildId === node.id;

    return (
      <div key={node.id} className="mb-1" style={{ marginLeft: depth * 16 }}>
        <div
          className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 transition-colors ${
            isSelected
              ? "bg-blue-600/30 border-blue-500"
              : isDisabled
                ? "bg-gray-800/50 border-gray-700 opacity-50"
                : "bg-gray-700/60 border-gray-600 hover:border-gray-500"
          }`}
        >
          {mayHaveChildren ? (
            <button
              type="button"
              onClick={() => toggleExpand(node.id)}
              disabled={isLoadingChildren}
              className="mt-0.5 w-6 h-6 flex-shrink-0 rounded bg-gray-600 hover:bg-gray-500 text-gray-200 flex items-center justify-center disabled:opacity-60"
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              {isLoadingChildren ? (
                <span className="w-3 h-3 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
            </button>
          ) : (
            <div className="w-6 flex-shrink-0" />
          )}

          <button
            type="button"
            disabled={isDisabled}
            onClick={() => {
              if (!isDisabled) setPendingParentId(node.id);
            }}
            className="flex-1 min-w-0 text-left disabled:cursor-not-allowed"
          >
            <div className="text-sm font-medium text-white truncate">
              {node.name || node.id}
            </div>
            {node.description ? (
              <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                {node.description}
              </div>
            ) : null}
            {isDisabled ? (
              <div className="text-[10px] text-amber-400 mt-0.5">
                Cannot select this node (would create a cycle)
              </div>
            ) : null}
          </button>

          {isSelected ? (
            <span className="text-xs text-blue-300 font-medium flex-shrink-0 mt-0.5">
              Selected
            </span>
          ) : null}
        </div>

        {isExpanded && childrenLoaded
          ? childList.map((child) => renderNode(child, depth + 1))
          : null}
      </div>
    );
  };

  if (!isOpen) return null;

  const heading =
    title ||
    (cfg
      ? `Select parent ${cfg.entityLabelLower}`
      : "Select parent");

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">{heading}</h2>
          <p className="text-sm text-gray-400 mt-1">
            Pick a node from the {cfg?.entityLabelLower || "taxonomy"} tree, or
            leave with no parent for a top-level item.
          </p>
        </div>

        <div className="px-5 py-3 border-b border-gray-700 flex flex-wrap gap-2 items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={cfg?.searchPlaceholder || "Search loaded nodes..."}
            className="flex-1 min-w-[12rem] bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
          />
          <button
            type="button"
            onClick={() => setPendingParentId("")}
            className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
              !pendingParentId
                ? "bg-blue-600/40 border-blue-500 text-white"
                : "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650"
            }`}
          >
            No parent (top level)
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error ? (
            <div className="mb-3 p-3 rounded-lg bg-red-900/40 border border-red-700 text-red-200 text-sm">
              {error}
            </div>
          ) : null}

          {loadingRoots ? (
            <div className="flex items-center justify-center py-12 text-gray-400 gap-3">
              <span className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              {cfg?.loadingLabel || "Loading..."}
            </div>
          ) : searchMatches ? (
            searchMatches.length === 0 ? (
              <p className="text-center text-gray-400 py-8 text-sm">
                No loaded nodes match your search. Expand the tree to load more,
                then search again.
              </p>
            ) : (
              <div className="space-y-1">
                <p className="text-xs text-gray-500 mb-2">
                  Showing matches among loaded nodes
                </p>
                {searchMatches.map((node) => {
                  const id = String(node.id);
                  const isDisabled = disabledIds.has(id);
                  const isSelected = pendingParentId === node.id;
                  return (
                    <button
                      key={node.id}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => {
                        if (!isDisabled) setPendingParentId(node.id);
                      }}
                      className={`w-full text-left rounded-lg border px-3 py-2 ${
                        isSelected
                          ? "bg-blue-600/30 border-blue-500"
                          : isDisabled
                            ? "opacity-50 border-gray-700"
                            : "bg-gray-700/60 border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      <div className="text-sm text-white font-medium">
                        {node.name || node.id}
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          ) : roots.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">
              {cfg?.emptyLabel || "No items found"}
            </p>
          ) : (
            <div>{roots.map((node) => renderNode(node, 0))}</div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
          >
            Use selected parent
          </button>
        </div>
      </div>
    </div>
  );
};

export default HierarchyParentPickerModal;
